import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { getApiErrorMessage } from '../../lib/apiError';
import { useProfile } from '../../hooks/useProfile';

export const ProfileDetailsCard: React.FC = () => {
  const { user, saveProfile } = useProfile();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Seed the fields once the cached user arrives, and re-seed if it changes
  // underneath us (a refresh elsewhere, or a save completing).
  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
  }, [user?.firstName, user?.lastName]);

  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const isUnchanged = trimmedFirst === user?.firstName && trimmedLast === user?.lastName;
  const isIncomplete = trimmedFirst.length < 2 || trimmedLast.length < 2;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSaving(true);

    try {
      await saveProfile(trimmedFirst, trimmedLast);
      setSuccess('Your details have been saved.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not save your details.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Details
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First name"
                fullWidth
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSaving}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last name"
                fullWidth
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSaving}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email address"
                fullWidth
                value={user?.email ?? ''}
                disabled
                helperText="Your email is your sign-in name and cannot be changed here."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Role"
                fullWidth
                value={user?.role === 'ADMIN' ? 'Administrator' : 'User'}
                disabled
                helperText="Only an administrator can change a role."
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving || isUnchanged || isIncomplete}
            >
              {isSaving ? <CircularProgress size={22} color="inherit" /> : 'Save changes'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
